import { useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { LeaveTypeSelect } from '@/components/selects';
import type { Employee360Response } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { downloadBlob } from '@/utils/downloadBlob';
import { RecordDataTable } from '@/components/ui/RecordDataTable';
import { notify } from '@/core/toast/notify';

export function HrEssPortalPage() {
  const [profile, setProfile] = useState<Employee360Response | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [leaveStart, setLeaveStart] = useState('');
  const [leaveEnd, setLeaveEnd] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [balances, setBalances] = useState<unknown[]>([]);
  const [applyingLeave, setApplyingLeave] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDesc, setTicketDesc] = useState('');

  const refresh = async () => {
    const p = await hrApi.essProfile();
    setProfile(p);
    if (p?.employee.id) {
      try {
        setBalances(await hrApi.leaveBalances(p.employee.id));
      } catch {
        setBalances([]);
      }
    }
  };

  useEffect(() => {
    refresh().catch((err) => showApiError(err, 'Failed to load ESS profile'));
  }, []);

  const geoCheckIn = async () => {
    if (!profile?.employee.id) return;
    setCheckingIn(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 });
      });
      await hrApi.markAttendance({
        employeeId: profile.employee.id,
        checkInMode: 'GEO',
        checkInLatitude: pos.coords.latitude,
        checkInLongitude: pos.coords.longitude,
      });
      notify.success('Geo check-in recorded');
      await refresh();
    } catch (err) {
      showApiError(err, 'Geo check-in failed — ensure location permission and geo-fence is configured');
    } finally {
      setCheckingIn(false);
    }
  };

  const applyLeave = async () => {
    if (!leaveTypeId || !leaveStart || !leaveEnd) {
      notify.error('Leave type, start, and end dates are required');
      return;
    }
    setApplyingLeave(true);
    try {
      await hrApi.essApplyLeave({
        leaveTypeId: Number(leaveTypeId),
        startDate: leaveStart,
        endDate: leaveEnd,
        reason: leaveReason || undefined,
      });
      notify.success('Leave request submitted');
      setLeaveReason('');
      await refresh();
    } catch (err) {
      showApiError(err, 'Leave application failed');
    } finally {
      setApplyingLeave(false);
    }
  };

  const raiseTicket = async () => {
    try {
      await hrApi.essRaiseTicket(ticketSubject, ticketDesc);
      notify.success('HR ticket submitted');
      setTicketSubject('');
      setTicketDesc('');
    } catch (err) {
      showApiError(err, 'Helpdesk ticket failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Employee Self Service"
        description="Profile, geo check-in, leave, attendance, and payslips"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'ESS' }]}
        actions={
          <Button variant="secondary" disabled={checkingIn} onClick={() => void geoCheckIn()}>
            {checkingIn ? 'Checking in…' : 'Geo check-in'}
          </Button>
        }
      />
      {profile && (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="card p-4">
            <h3 className="font-semibold">My profile</h3>
            <p className="text-sm">
              {profile.employee.firstName} {profile.employee.lastName ?? ''} ({profile.employee.employeeCode})
            </p>
            <p className="text-sm text-slate-600">{profile.employee.email}</p>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Apply leave</h3>
            <div className="mt-2 space-y-2">
              <LeaveTypeSelect value={leaveTypeId} onChange={setLeaveTypeId} required />
              <DateRangeFields
                from={leaveStart}
                to={leaveEnd}
                onFromChange={setLeaveStart}
                onToChange={setLeaveEnd}
                fromLabel="Start date"
                toLabel="End date"
              />
              <Input label="Reason" value={leaveReason} onChange={(e) => setLeaveReason(e.target.value)} />
              <Button loading={applyingLeave} onClick={() => void applyLeave()}>
                Submit leave
              </Button>
            </div>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Leave balances</h3>
            <RecordDataTable data={balances} embedded emptyTitle="No leave balances" />
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Leave history</h3>
            <ul className="text-sm">
              {(profile.recentLeaves ?? []).map((l) => (
                <li key={l.id}>
                  {l.leaveType}: {l.status}
                </li>
              ))}
            </ul>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Recent attendance</h3>
            <ul className="text-sm">
              {(profile.recentAttendance ?? []).map((a) => (
                <li key={a.id}>
                  {a.date}: {a.status}
                </li>
              ))}
            </ul>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">HR helpdesk</h3>
            <Input label="Subject" value={ticketSubject} onChange={(e) => setTicketSubject(e.target.value)} />
            <Input label="Description" value={ticketDesc} onChange={(e) => setTicketDesc(e.target.value)} />
            <Button className="mt-2" onClick={() => void raiseTicket()}>Raise ticket</Button>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Payslips</h3>
            <ul className="text-sm">
              {(profile.recentPayroll ?? []).map((p) => (
                <li key={p.runId} className="flex items-center justify-between gap-2">
                  <span>
                    Run {p.runCode}: {p.netAmount}
                  </span>
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      try {
                        const blob = await hrApi.downloadEssPayslipPdf(p.runId);
                        downloadBlob(blob, `payslip-${p.runCode}.pdf`);
                      } catch (err) {
                        showApiError(err, 'Payslip download failed');
                      }
                    }}
                  >
                    PDF
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
